import { ServicesFilter, defaultServicesFilter } from '../models';
import { useQueryParams, usePagination } from '@/hooks';
import useDeleteService from './useDeleteService';
import { useState, useEffect, useCallback } from 'react';

interface DeleteModalState {
  open: boolean;
  serviceId: string;
  serviceName: string;
}

const useServicesPage = () => {
  const { getQueryParam, setQueryParams } = useQueryParams();
  const { executeDelete, isDeleting } = useDeleteService();

  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    open: false,
    serviceId: '',
    serviceName: '',
  });

  const [filters, setFilters] = useState<ServicesFilter>(() => ({
    name: getQueryParam('name') ?? defaultServicesFilter.name,
    serviceTypeId: getQueryParam('serviceTypeId') ?? defaultServicesFilter.serviceTypeId,
    vehicleTypeId: getQueryParam('vehicleTypeId') ?? defaultServicesFilter.vehicleTypeId,
    commercialBusinessId: getQueryParam('commercialBusinessId') ?? defaultServicesFilter.commercialBusinessId,
  }));

  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } =
    usePagination({
      initialPage: Number(getQueryParam('page')) || 1,
      initialPageSize: Number(getQueryParam('pageSize')) || 10,
    });

  useEffect(() => {
    setQueryParams({
      name: filters.name || undefined,
      serviceTypeId: filters.serviceTypeId || undefined,
      vehicleTypeId: filters.vehicleTypeId || undefined,
      commercialBusinessId: filters.commercialBusinessId || undefined,
      page: currentPage.toString(),
      pageSize: pageSize.toString(),
    });
  }, [filters, currentPage, pageSize, setQueryParams]);

  const openDeleteModal = useCallback((serviceId: string, serviceName: string) => {
    setDeleteModal({ open: true, serviceId, serviceName });
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (!isDeleting) {
      setDeleteModal({ open: false, serviceId: '', serviceName: '' });
    }
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(() => {
    executeDelete(deleteModal.serviceId).finally(() => {
      setDeleteModal({ open: false, serviceId: '', serviceName: '' });
    });
  }, [deleteModal.serviceId, executeDelete]);

  return {
    filters,
    setFilters,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    deleteModal,
    openDeleteModal,
    closeDeleteModal,
    handleConfirmDelete,
    isDeleting,
  };
};

export default useServicesPage;
